import { FormOption } from '@qfa/forms';
import { KineticModule, PrivilegeType } from '@kinetic-ui/data-layer';
import React, { lazy } from 'react';
import { Override } from 'types/util';
import { getEnvironmentModulesList } from './getEnvironmentModulesList';

export type EXTENDED_KINETIC_MODULES = 'app' | KINETIC_MODULES;

export type KINETIC_MODULES =
  | 'communicationlogs'
  | 'connectedvehicles'
  | 'contacts'
  | 'corridors'
  | 'detectors'
  | 'emails'
  | 'events'
  | 'fleet'
  | 'parking'
  | 'ramp-meter'
  | 'roadassistance'
  | 'integrations'
  | 'locations'
  | 'shared'
  | 'signals'
  | 'signs'
  | 'tolling'
  | 'transit'
  | 'travel-time'
  | 'tunnels'
  | 'video'
  | 'weather'
  | 'gates'
  | 'har'
  | 'report-builder'
  | 'road-conditions'
  | 'assetmanagement'
  | 'tolling';

const envModules = getEnvironmentModulesList();

const defaultComponent = () => null;
defaultComponent.__missedImport = true;

export const onComponentImportError = (_e: Error) => {
  // console.warn(e);
  return { default: defaultComponent };
};

export const onWidgetImportError = (_e: Error) => {
  // console.warn(e);
  return { default: [] };
};

export const onArrayImportError = (_e: Error) => {
  // console.warn(e);
  return { default: [] };
};

export type ModuleImportPromise<TValue> = Promise<{ default: TValue }>;
export type LazyComponent = React.LazyExoticComponent<React.ComponentType<any>>;
export type ModuleImports<TValue> = Record<KINETIC_MODULES, TValue>;

export const renderLazyModules = <TProps,>(
  imports: ModuleImports<React.LazyExoticComponent<React.ComponentType<any>>>,
  props?: TProps
) => {
  return Object.keys(imports).map((key) => {
    const name = key as KINETIC_MODULES;
    const Component = imports[name];
    return <Component key={name} {...props} />;
  }) as Override<JSX.Element, { key: KINETIC_MODULES }>[];
};

export const getPromisedModules = async <TValue,>(
  imports: ModuleImports<ModuleImportPromise<TValue>>
) => {
  const promises = Object.keys(imports).map((key) => {
    return imports[key as KINETIC_MODULES];
  });
  const resolved = await Promise.all(promises);
  const results = Object.keys(imports).reduce((acc, key, index) => {
    acc[key as unknown as KINETIC_MODULES] = resolved[index].default;
    return acc;
  }, {} as ModuleImports<TValue>);
  return results;
};

/**
 * **README**
 * !! PLEASE DO NOT CREATE ModuleImports RECORDS IN SHARED !!
 * Shared should not know about modules, import from them, or define the app's feature concerns (like dynamic module imports and locations)
 *
 * If you are considering editing this file, You probably don't know what you are doing.
 * Please get help.
 *
 * SEE: packages\app\src\kinetic\modules.tsx
 *
 * Dynamic module imports are an app construct. They have no place in shared.
 *
 * The proper way to execute dynamic module imports is as follows
 *
 * 1. Created shared component for UI concerns of the feature that is to be shared by the modules.
 *    For example: StatusBar
 *
 * 2. add a dynamic module import entry to packages\app\src\kinetic\modules.tsx
 *    These imports will attempt to import a feature implementation component from each module.
 *    If the import doesn't exist for every module, that is OK and expected, and the app will log a warning and move on.
 *
 * 3. Create a module feature implementation component using the shared UI component
 *    For example: packages\signs\src\components\maps\layers\StatusBar\StatusBar.tsx
 *
 * 4. Export the module feature from the expected location in the module's kinetic folder
 *    This expected location is the location you defined in packages\app\src\kinetic\modules.tsx
 *
 * 5. Create a feature encapsulation component that executes:
 *    renderLazyModules, renderLazyModule, or getPromisedModules, depending on your needs.
 *    For example: packages\app\src\App\menus\appDrawerMenus\MainMenu\MainMenu.tsx
 *
 * TECH_DEBT: Delete this file and migrate the relevant code to app.
 */

export const MapLayers = envModules.reduce((acc, name) => {
  acc[name] = lazy(() =>
    import(
      /* webpackMode: 'lazy-once', webpackPreload:true, webpackChunkName: 'MapLayers' */ `@kinetic-ui/${name}/lib/kinetic/Layers`
    ).catch(onComponentImportError)
  );
  return acc;
}, {} as ModuleImports<LazyComponent>);

export const MapContextMenuList = envModules.reduce((acc, name) => {
  acc[name] = import(
    /* webpackMode: 'lazy-once', webpackPrefetch:true, webpackChunkName: 'MapContextMenu' */ `@kinetic-ui/${name}/lib/kinetic/MapContextMenuList`
  ).catch(onWidgetImportError);
  return acc;
}, {} as ModuleImports<ModuleImportPromise<{ name: string; to: string; module?: KineticModule; privilegeType?: PrivilegeType }[]>>);

export const MapLegends = envModules.reduce((acc, name) => {
  acc[name] = lazy(() =>
    import(
      /* webpackMode: 'lazy-once', webpackPreload:true, webpackChunkName: 'MapLegend' */ `@kinetic-ui/${name}/lib/kinetic/MapLegend`
    ).catch(onComponentImportError)
  );
  return acc;
}, {} as ModuleImports<LazyComponent>);

export const StatusBars = envModules.reduce((acc, name) => {
  acc[name] = lazy(() =>
    import(
      /*  webpackMode: 'lazy-once', webpackPrefetch: true, webpackChunkName: 'StatusBar' */ `@kinetic-ui/${name}/lib/kinetic/StatusBar`
    ).catch(onComponentImportError)
  );
  return acc;
}, {} as ModuleImports<LazyComponent>);

export const WidgetsRoutePathList = envModules.reduce((acc, name) => {
  acc[name] = import(
    /* webpackMode: 'lazy-once', webpackPrefetch:true, webpackChunkName: 'WidgetConfigs' */ `@kinetic-ui/${name}/lib/kinetic/Widgets`
  ).catch(onWidgetImportError);
  return acc;
}, {} as ModuleImports<ModuleImportPromise<{ name: string; to: string }[]>>);

export const StaticEventFields = envModules.reduce((acc, name) => {
  acc[name] = import(
    /* webpackMode: 'lazy-once', webpackPrefetch:true, webpackChunkName: 'StaticEventFieldsConfig' */ `@kinetic-ui/${name}/lib/kinetic/StaticEventFields`
  ).catch(onWidgetImportError);
  return acc;
}, {} as ModuleImports<ModuleImportPromise<{ name: string; type: string; options?: FormOption[] }[]>>);

export const IncidentDetailsField = envModules.reduce((acc, name) => {
  acc[name] = lazy(() =>
    import(
      /* webpackMode: 'lazy-once', webpackPreload:true, webpackChunkName: 'IncidentDetailsField' */ `@kinetic-ui/${name}/lib/kinetic/IncidentDetailsField`
    ).catch(onComponentImportError)
  );
  return acc;
}, {} as ModuleImports<LazyComponent>);

export const MapSearchResult = envModules.reduce((acc, name) => {
  acc[name] = lazy(() =>
    import(
      /* webpackMode: 'eager', webpackPreload: true, webpackChunkName: 'MapSearchResult' */ `@kinetic-ui/${name}/lib/kinetic/MapSearchResult`
    ).catch(onComponentImportError)
  );
  return acc;
}, {} as ModuleImports<LazyComponent>);
